import React, {RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {alpha, Box, Typography, useTheme} from '@mui/material'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  formatAssumptionTime,
  formatSecondaryAssumptionState,
  getSecondaryAssumptionKey,
  getSecondaryAssumptionStateTone,
  SecondaryAssumption,
} from 'src/constants/secondaryAssumptions'

// Deliberately slow: the point is for the patient to follow the dose with their eyes from the
// "Prossime assunzioni" card down into the history.
export const FLIGHT_MS = 1700
const SHIFT_MS = 1000
const FLASH_MS = 3000
const GHOST_FADE_MS = 300

export interface Flight {
  id: number
  // the dose as it was in the card: its key there, its slot, its therapy
  source: SecondaryAssumption
  sourceIndex: number
  // key of the history row the answered dose becomes (it gets an `_id` once answered)
  targetKey: string
  from: DOMRect
  drugName: string
  answered: SecondaryAssumption
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

function canAnimate(element: Element): boolean {
  return typeof element.animate === 'function'
}

// `getBoundingClientRect` top of every history row, to slide the others out of the way (FLIP)
function snapshotRowTops(container: HTMLElement | null): Map<string, number> {
  const tops = new Map<string, number>()
  container?.querySelectorAll<HTMLElement>('[data-row-key]').forEach((row) => {
    tops.set(row.dataset.rowKey as string, row.getBoundingClientRect().top)
  })
  return tops
}

const findRow = (container: HTMLElement | null, key: string): HTMLElement | null =>
  container?.querySelector<HTMLElement>(`[data-row-key="${key}"]`) ?? null

const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/**
 * Animates an answered "Prossime assunzioni" dose moving into the history table below: a copy of
 * the dose, already showing its new state, flies from its card row to the table row it became,
 * the other rows slide down to make room, and the row lights up once it lands.
 *
 * The target row is looked up on every frame rather than measured once: right after the answer
 * the page is refetched (the following dose comes back and the card may resize), so the row can
 * still move while the copy is in flight. When the row isn't on screen (another page, or a filter
 * hides it) the copy just fades out where it is. Skipped entirely under prefers-reduced-motion.
 */
export default function useFlyToHistory(tableRef: RefObject<HTMLElement>): {
  flight?: Flight
  launch: (source: SecondaryAssumption, sourceIndex: number, answered: SecondaryAssumption, drugName: string) => void
  flightLayer: React.ReactNode
} {
  const [flight, setFlight] = useState<Flight>()
  const rowTopsBefore = useRef<Map<string, number>>(new Map())
  const nextId = useRef(0)

  const launch = useCallback((source: SecondaryAssumption, sourceIndex: number, answered: SecondaryAssumption, drugName: string) => {
    const sourceKey = getSecondaryAssumptionKey(source)
    const sourceRow = document.querySelector<HTMLElement>(`[data-next-dose-key="${sourceKey}"]`)
    if (!sourceRow || !canAnimate(sourceRow) || prefersReducedMotion()) return
    rowTopsBefore.current = snapshotRowTops(tableRef.current)
    nextId.current += 1
    setFlight({
      id: nextId.current,
      source,
      sourceIndex,
      targetKey: getSecondaryAssumptionKey(answered),
      from: sourceRow.getBoundingClientRect(),
      drugName,
      answered,
    })
  }, [tableRef])

  // The answered dose now sits in the table: slide the rows that were already there from where
  // they were to where they are.
  useLayoutEffect(() => {
    if (!flight) return
    tableRef.current?.querySelectorAll<HTMLElement>('[data-row-key]').forEach((row) => {
      const before = rowTopsBefore.current.get(row.dataset.rowKey as string)
      if (before === undefined || row.dataset.rowKey === flight.targetKey || !canAnimate(row)) return
      const delta = before - row.getBoundingClientRect().top
      if (delta) {
        row.animate([{transform: `translateY(${delta}px)`}, {transform: 'none'}], {duration: SHIFT_MS, easing: 'cubic-bezier(.2,.8,.2,1)'})
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight?.id])

  const onLanded = useCallback(() => setFlight(undefined), [])

  return {
    flight,
    launch,
    flightLayer: flight ? <FlyingDose key={flight.id} flight={flight} tableRef={tableRef} onDone={onLanded} /> : null,
  }
}

function FlyingDose({flight, tableRef, onDone}: {flight: Flight; tableRef: RefObject<HTMLElement>; onDone: () => void}): JSX.Element {
  const ghostRef = useRef<HTMLDivElement>(null)
  // the landed row lights up in the program's own color, then fades back to the other rows'
  const highlight = alpha(useTheme().palette.primary.main, 0.25)
  const {from, targetKey, answered} = flight

  useEffect(() => {
    const ghost = ghostRef.current
    if (!ghost) {
      onDone()
      return undefined
    }
    // rows hidden while the copy is in flight (React may swap the element under us on refetch)
    const hidden = new Set<HTMLElement>()
    const restore = (): void => {
      hidden.forEach((row) => { row.style.opacity = '' })
      hidden.clear()
    }
    let frame = 0
    let finished = false
    const start = performance.now()

    const place = (top: number, left: number, width: number, height: number): void => {
      Object.assign(ghost.style, {top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px`})
    }

    const finish = (row: HTMLElement | null): void => {
      finished = true
      restore()
      if (row && canAnimate(row)) {
        const rest = getComputedStyle(row).backgroundColor
        row.animate([
          {backgroundColor: highlight},
          {backgroundColor: highlight, offset: 0.35},
          {backgroundColor: rest},
        ], {duration: FLASH_MS, easing: 'ease-out'})
      }
      const fade = ghost.animate([{opacity: 1}, {opacity: 0}], {duration: GHOST_FADE_MS, fill: 'forwards'})
      fade.onfinish = onDone
    }

    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / FLIGHT_MS)
      const row = findRow(tableRef.current, targetKey)
      if (row) {
        hidden.forEach((other) => { if (other !== row) other.style.opacity = '' })
        row.style.opacity = '0'
        hidden.add(row)
        const to = row.getBoundingClientRect()
        const e = easeInOut(t)
        place(lerp(from.top, to.top, e), lerp(from.left, to.left, e), lerp(from.width, to.width, e), lerp(from.height, to.height, e))
        // a slight lift at take-off, so it reads as the same dose being picked up
        ghost.style.transform = `scale(${1 + 0.03 * Math.sin(Math.PI * Math.min(1, t * 2.5))})`
      }
      if (t < 1) {
        frame = requestAnimationFrame(step)
      } else {
        finish(row)
      }
    }

    place(from.top, from.left, from.width, from.height)
    frame = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(frame)
      if (!finished) restore()
    }
  }, [from, targetKey, tableRef, onDone, highlight])

  const shownDate = answered.takenAt ?? answered.expectedDate
  // react-dom's portal, not MUI's: MUI's mounts its children one render later, after this
  // component's effect has already run without a ghost to move.
  return createPortal(
    <Box
      ref={ghostRef}
      aria-hidden
      data-testid="flying-dose"
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.modal + 1,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        overflow: 'hidden',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 12px 32px rgba(44, 62, 80, 0.22)',
      }}
    >
      <Box sx={{minWidth: 0}}>
        <Typography noWrap sx={{fontWeight: 600, color: NEUTRAL_SCALE[700]}}>{flight.drugName}</Typography>
        <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500]}}>{formatAssumptionTime(shownDate)}</Typography>
      </Box>
      <StatusPill label={formatSecondaryAssumptionState(answered.state)} color={getSecondaryAssumptionStateTone(answered.state)} />
    </Box>,
    document.body
  )
}
